// Onboarding API key hook — fetches or creates a test API key for onboarding

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiKey, CreateApiKeyResponse } from './use-api-keys'

interface ApiKeyListResponse {
  data: ApiKey[]
}

const QUERY_KEY = ['onboarding-test-key'] as const

/**
 * Fetches the user's test API key for onboarding code snippets.
 * If no test key exists, returns null — the UI should show a "create" button.
 */
export function useTestApiKey(): ReturnType<typeof useQuery<ApiKey | null>> {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const result = await apiClient.get<ApiKeyListResponse>(endpoints.apiKeys.list)
      const testKey = result.data.find((k) => k.type === 'test')
      return testKey ?? null
    },
  })
}

/**
 * Creates a test API key for the onboarding flow.
 * Returns the full key (visible only once).
 */
export function useCreateTestApiKey(): ReturnType<
  typeof useMutation<CreateApiKeyResponse, unknown, void>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient.post<CreateApiKeyResponse>(endpoints.apiKeys.create, {
        name: 'Onboarding Test Key',
        scope: 'send',
        type: 'test',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
      qc.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })
}
